import React, { useCallback, useEffect, useMemo, useState } from 'react'
import styled from 'styled-components'
import { theme } from 'styled-tools'
import QuestionBlock from './QuestionBlock'
import FormQuestionBlock from './FormQuestionBlock'
import { isEmbedded } from '../../containers/bada/utils'

const QuestionBlockWrapper = styled.div`
  display: flex;
  flex-direction: column;
  color: ${theme('colors.link')};
  font-size: 14px;
  @media (min-width: 720px) {
    font-size: 16px;
  }
  margin-top: 30px;
`

/**
 * @param {{
 *  questions: any[],
 *  updateUiState: (params: any[]) => any,
 *  prepData: any,
 *  onChange: (data: any) => void
 *  onEmpty: () => void
 *  onLoading: () => void
 * }} params
 */
const QuickQuestions = ({
  questions,
  updateUiState,
  prepData,
  onChange,
  onEmpty,
  onLoading,
  isBookingPage,
  useShowHideQuestionFeature,
}) => {
  const [sentEmpty, setSentEmpty] = useState(false)
  const onUpdate = useCallback(
    (id, questionText, value) => {
      const prepQuestions = prepData.questions || []
      const updatedQuestion = {
        id,
        question: questionText,
        answers: value,
      }

      const index = prepQuestions.findIndex(
        (p) => p.id === id || p.question === questionText
      )
      const updatedQuestions =
        index === -1
          ? [...prepQuestions, updatedQuestion]
          : [
              ...prepQuestions.slice(0, index),
              updatedQuestion,
              ...prepQuestions.slice(index + 1),
            ]
      const updatedPrepData = { ...prepData, questions: updatedQuestions }
      updateUiState({
        prepData: updatedPrepData,
      })
      onChange(updatedPrepData)
    },
    [onChange, updateUiState, prepData]
  )

  useEffect(() => {
    if (!questions?.length) {
      return
    }
    if (!prepData?.questions?.length) {
      const updatedPrepData = {
        questions: questions.map((q) => ({
          id: q.id,
          question: q.questionText,
          answers: [],
        })),
      }
      onChange(updatedPrepData)
      updateUiState({ prepData: updatedPrepData })
    }
  }, [prepData, questions, onChange, updateUiState])

  const showQuestions = useMemo(() => {
    if (isBookingPage) {
      return (
        useShowHideQuestionFeature && questions.some((q) => q.showOnBookingPage)
      )
    }
    return (
      !useShowHideQuestionFeature ||
      questions.some((q) => !q.hideOnPrepFormPage)
    )
  }, [questions, isBookingPage, useShowHideQuestionFeature])

  useEffect(() => {
    if (showQuestions) {
      return
    }
    if (!onEmpty) {
      return
    }
    if (isEmbedded()) {
      return
    }
    if (sentEmpty) {
      return
    }
    setSentEmpty(true)
    onEmpty()
  }, [showQuestions, onEmpty, sentEmpty])

  if (!showQuestions) {
    return null
  }

  return (
    <QuestionBlockWrapper data-track="questions">
      {questions?.map((question) => {
        const sortOrder = questions.findIndex(
          (q) =>
            q.questionText === question.questionText || q.id === question.id
        )
        const answers =
          prepData.questions.find(
            (q) => q.question === question.questionText || q.id === question.id
          )?.answers || []

        if (!isBookingPage) {
          return (
            <QuestionBlock
              key={question.questionText}
              question={{ ...question, sortOrder }}
              answers={answers}
              onLoading={onLoading}
              onUpdate={(value) =>
                onUpdate(question.id, question.questionText, value)
              }
              useShowHideQuestionFeature={useShowHideQuestionFeature}
            />
          )
        }

        const show = !!question.showOnBookingPage
        if (!show) {
          return null
        }

        return (
          <FormQuestionBlock
            key={question.questionText}
            question={{ ...question, sortOrder }}
            answers={answers}
            onLoading={onLoading}
            onUpdate={(value) =>
              onUpdate(question.id, question.questionText, value)
            }
          />
        )
      })}
    </QuestionBlockWrapper>
  )
}

export default QuickQuestions
